import {
  ContractsCompensationsTypesEnum,
  ContractsValueObjectsTimeConditionEnum,
} from 'generated/resources/graphql'

import type CompensationModel from 'modules/contracts/store/models/Compensation'
import { UNITS_TYPE_UNITS } from 'modules/contracts/store/models/Compensation'

export const NO_POLICY_ID = -1

export interface EmployeeMeta {
  id: number
  legalEntityId: number
  payrollPolicyId: number
  activeContractId?: number
  canEdit: boolean
  avatar?: string
  name: string
}

export type EmployeeMetaMap = { [id: string]: EmployeeMeta }

export interface LegalEntityMeta {
  id: number
  currency: string | null
  name: string
}

export interface PayrollPolicyMeta {
  id: number
  name: string

  /**
   * The periodicity at which the policy period recurs. Not to be confused with
   * the recurrence of compensations between periods.
   * @see Policy.periodicities for example of what this looks like
   */
  recurrence: string

  /**
   * Necessary to ensure valid dates for compensations sync'd with payroll.
   */
  startsOn: string
}

export type CompensationRateType = ContractsCompensationsTypesEnum
export type CalculationType = keyof typeof CompensationModel.calculations
export type TimeConditionType = ContractsValueObjectsTimeConditionEnum
/**
 * Either `units` or the LE's currency string
 */
export type CompensationUnit = typeof UNITS_TYPE_UNITS | string

// FIXME: These are placeholders until the graphql schema is generated

export interface CompensationTemplateDTO {
  contractsTaxonomyId: number
  description?: string
  compensationType: CompensationRateType
  amount?: string
  unit: CompensationUnit

  /**
   * @requires `recurrenceCount` and `startsOn` MUST also be sent if `true`, else
   * `recurrence` and `firstPaymentOn` MUST be sent.
   */
  syncWithSupplements: boolean

  // `syncWithSupplements` is checked...
  /**
   * How frequently the compensation will be generated. REQUIRED if `syncWithSupplements` is true.
   */
  recurrenceCount?: number
  /**
   * The date the compensation will start generating. Must coincide with the payroll
   * policy start dates.
   *
   * REQUIRED if `syncWithSupplements` is true.
   */
  startsOn?: string // date

  // Otherwise
  /**
   * Incidcates the monthly recurrence of the compensation. REQUIRED if `syncWithSupplements` is false.
   *
   * @see Compensation.recurrences
   */
  recurrence: string

  /**
   * The date the first payment will be made. Will be set to start of the month.
   */
  firstPaymentOn?: string // date

  calculation: CalculationType

  /**
   * The time condition for the compensation. REQUIRED if `compensation_type` is `per_worked_day`.
   */
  timeCondition?: TimeConditionType

  /**
   * The minimum amount of hours an employee should work to be eligible for a compensation for the day.
   * REQUIRED if `timeCondition` is `custom`.
   */
  minimumAmountOfHours?: number
}

export type CompensationTemplateUI = CompensationTemplateDTO & {
  taxonomyName: string
  legalEntityName: string
  policyName: string
}

export interface CompensationEmployeeMapUI {
  employeeIds: number[]
  compensationTemplate: CompensationTemplateUI
}

export interface DraftCompensation extends Partial<CompensationTemplateDTO> {
  taxonomyName?: string
  legalEntityId?: number
  policyId?: number
}
